import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Checkbox,
  Select,
  message,
  Row,
  Col,
  Input,
  Tag,
  Empty,
  Spin,
} from 'antd';
import {
  SafetyCertificateOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  UserOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import userService from '../services/userService';
import { notify } from '../components/Customs/NotifyProvider';

const { Option } = Select;

const REQUIRED_MODULES = [
  
];

const mergeRequiredModules = (apiModules = []) => {
  const byKey = new Map();

  (Array.isArray(apiModules) ? apiModules : []).forEach((module) => {
    if (module?.module_key) byKey.set(module.module_key, module);
  });

  REQUIRED_MODULES.forEach((module) => {
    if (!byKey.has(module.module_key)) {
      byKey.set(module.module_key, module);
    }
  });

  return Array.from(byKey.values()).sort(
    (a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)
  );
};

const UserPermission = () => {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState(null);

  const [permissionMap, setPermissionMap] = useState({});

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState('');

  // =========================
  // LOAD USERS
  // =========================
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await userService.listUsers();

      if (res?.status) {
        setUsers(res.data || []);
      } else {
        notify.error(res?.message || 'Failed to load users');
      }
    } catch (error) {
      notify.error(error?.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // =========================
  // LOAD MODULES
  // =========================
  const loadModules = async () => {
    try {
      setLoadingModules(true);
      const res = await userService.listModules();

      if (res?.status) {
        setModules(mergeRequiredModules(res.data || []));
      } else {
        notify.error(res?.message || 'Failed to load modules');
        setModules(mergeRequiredModules([]));
      }
    } catch (error) {
      notify.error(error?.message || 'Failed to load modules');
      setModules(mergeRequiredModules([]));
    } finally {
      setLoadingModules(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadModules();
  }, []);

  // =========================
  // DEFAULT BLANK MAP
  // =========================
  const createBlankPermissionMap = (moduleList) => {
    const map = {};
    moduleList.forEach((module) => {
      map[module.module_key] = {
        module_key: module.module_key,
        can_view: false,
        can_create: false,
        can_update: false,
        can_delete: false,
      };
    });
    return map;
  };

  // =========================
  // LOAD USER PERMISSIONS
  // =========================
  const loadUserPermissions = async (userId) => {
    if (!userId) return;

    try {
      setLoadingPermissions(true);

      const blankMap = createBlankPermissionMap(modules);

      const res = await userService.getUserPermissions(userId);

      if (res?.status) {
        const existingPermissions = res.data || [];

        existingPermissions.forEach((item) => {
          blankMap[item.module_key] = {
            module_key: item.module_key,
            can_view: Number(item.can_view) === 1,
            can_create: Number(item.can_create) === 1,
            can_update: Number(item.can_update) === 1,
            can_delete: Number(item.can_delete) === 1,
          };
        });

        setPermissionMap(blankMap);
      } else {
        setPermissionMap(blankMap);
        notify.error(res?.message || 'Failed to load permissions');
      }
    } catch (error) {
      setPermissionMap(createBlankPermissionMap(modules));
      notify.error(error?.message || 'Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  // =========================
  // HANDLE USER CHANGE
  // =========================
  const handleUserChange = async (userId) => {
    setSelectedUserId(userId);

    const foundUser = users.find((u) => String(u.user_id) === String(userId));
    setSelectedUser(foundUser || null);

    if (modules.length > 0) {
      await loadUserPermissions(userId);
    }
  };

  // if user already selected and modules came later
  useEffect(() => {
    if (selectedUserId && modules.length > 0) {
      loadUserPermissions(selectedUserId);
    }
  }, [modules]);

  // =========================
  // HANDLE CHECKBOX
  // =========================
  const handlePermissionChange = (moduleKey, field, checked) => {
    setPermissionMap((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [field]: checked,
      },
    }));
  };

  const handleRowAllChange = (moduleKey, checked) => {
    setPermissionMap((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        can_view: checked,
        can_create: checked,
        can_update: checked,
        can_delete: checked,
      },
    }));
  };

  const handleEnableAll = () => {
    const updated = {};
    modules.forEach((module) => {
      updated[module.module_key] = {
        module_key: module.module_key,
        can_view: true,
        can_create: true,
        can_update: true,
        can_delete: true,
      };
    });
    setPermissionMap(updated);
  };

  const handleClearAll = () => {
    const updated = {};
    modules.forEach((module) => {
      updated[module.module_key] = {
        module_key: module.module_key,
        can_view: false,
        can_create: false,
        can_update: false,
        can_delete: false,
      };
    });
    setPermissionMap(updated);
  };

  // =========================
  // SAVE
  // =========================
  const handleSave = async () => {
    if (!selectedUserId) {
      notify.warning('Please select a user first');
      return;
    }

    try {
      setSaving(true);

      const permissions = modules.map((module) => ({
        module_key: module.module_key,
        can_view: permissionMap[module.module_key]?.can_view ? 1 : 0,
        can_create: permissionMap[module.module_key]?.can_create ? 1 : 0,
        can_update: permissionMap[module.module_key]?.can_update ? 1 : 0,
        can_delete: permissionMap[module.module_key]?.can_delete ? 1 : 0,
      }));

      const res = await userService.saveUserPermissions(selectedUserId, permissions);

      if (res?.status) {
        notify.success(res?.message || 'Permissions saved successfully');
        await loadUserPermissions(selectedUserId);
      } else {
        notify.error(res?.message || 'Failed to save permissions');
      }
    } catch (error) {
      notify.error(
        error?.response?.data?.message || error?.message || 'Failed to save permissions'
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // REFRESH
  // =========================
  const handleRefresh = async () => {
    await Promise.all([loadUsers(), loadModules()]);
    if (selectedUserId && modules.length > 0) {
      await loadUserPermissions(selectedUserId);
    }
  };

  // =========================
  // FILTERED MODULES
  // =========================
  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const q = searchText.toLowerCase();
      return (
        !searchText ||
        module.module_name?.toLowerCase().includes(q) ||
        module.module_key?.toLowerCase().includes(q) ||
        module.route_path?.toLowerCase().includes(q)
      );
    });
  }, [modules, searchText]);

  const totalModules = modules.length;
  const enabledModules = modules.filter((module) => {
    const p = permissionMap[module.module_key];
    return p && (p.can_view || p.can_create || p.can_update || p.can_delete);
  }).length;

  const columns = [
    {
      title: 'Module',
      dataIndex: 'module_name',
      key: 'module_name',
      render: (_, record) => (
        <div>
          <div className="permission-module-title">{record.module_name}</div>
          <div className="permission-module-subtitle">
            {record.module_key} {record.route_path ? `• ${record.route_path}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const p = permissionMap[record.module_key];
        const enabled =
          p?.can_view || p?.can_create || p?.can_update || p?.can_delete;

        return (
          <Tag color={enabled ? 'success' : 'default'}>
            {enabled ? 'Enabled' : 'Disabled'}
          </Tag>
        );
      },
    },
    {
      title: 'View',
      key: 'can_view',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={!!permissionMap[record.module_key]?.can_view}
          onChange={(e) =>
            handlePermissionChange(record.module_key, 'can_view', e.target.checked)
          }
        />
      ),
    },
    {
      title: 'Create',
      key: 'can_create',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={!!permissionMap[record.module_key]?.can_create}
          onChange={(e) =>
            handlePermissionChange(record.module_key, 'can_create', e.target.checked)
          }
        />
      ),
    },
    {
      title: 'Update',
      key: 'can_update',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={!!permissionMap[record.module_key]?.can_update}
          onChange={(e) =>
            handlePermissionChange(record.module_key, 'can_update', e.target.checked)
          }
        />
      ),
    },
    {
      title: 'Delete',
      key: 'can_delete',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={!!permissionMap[record.module_key]?.can_delete}
          onChange={(e) =>
            handlePermissionChange(record.module_key, 'can_delete', e.target.checked)
          }
        />
      ),
    },
    {
      title: 'All',
      key: 'all',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const p = permissionMap[record.module_key] || {};
        const allSelected =
          p.can_view && p.can_create && p.can_update && p.can_delete;

        return (
          <Checkbox
            checked={!!allSelected}
            onChange={(e) => handleRowAllChange(record.module_key, e.target.checked)}
          />
        );
      },
    },
  ];

  const pageLoading = loadingUsers || loadingModules;

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <SafetyCertificateOutlined />
          </div>

          <div>
            <h2 className="user-page-title">User Permission</h2>
            <p className="user-page-subtitle">
              Assign module-wise access controls for each user.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            icon={<ReloadOutlined />}
            className="secondary-btn"
            onClick={handleRefresh}
            loading={pageLoading || loadingPermissions}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            className="primary-btn"
            onClick={handleSave}
            loading={saving}
          >
            Save Permissions
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]} className="user-stats-row">
        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon blue">
              <AppstoreOutlined />
            </div>
            <div>
              <div className="stats-card-label">Total Modules</div>
              <div className="stats-card-value">{totalModules}</div>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon green">
              <CheckCircleOutlined />
            </div>
            <div>
              <div className="stats-card-label">Enabled Modules</div>
              <div className="stats-card-value">{enabledModules}</div>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon red">
              <UserOutlined />
            </div>
            <div>
              <div className="stats-card-label">Selected User</div>
              <div className="stats-card-value">
                {selectedUser?.username || 'None'}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <div className="page-card filter-card">
        <div className="filter-card-header">
          <h3>Permission Filters</h3>
          <span>{filteredModules.length} modules found</span>
        </div>

        <div className="filters-inline-row">
          <div className="filter-item user-filter-item">
            <label className="field-label">Select User</label>
            <Select
              placeholder="Choose user"
              className="page-select w-full"
              value={selectedUserId}
              onChange={handleUserChange}
              loading={loadingUsers}
              showSearch
              optionFilterProp="children"
            >
              {users.map((user) => (
                <Option key={user.user_id} value={user.user_id}>
                  {user.full_name || user.username} ({user.username})
                </Option>
              ))}
            </Select>
          </div>

          <div className="filter-item module-search-item">
            <label className="field-label">Search Module</label>
            <Input
              placeholder="Search by module name / key / route"
              className="page-search"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="filter-item action-btn-item">
            <label className="field-label">Quick Action</label>
            <Button className="secondary-btn w-full" onClick={handleEnableAll}>
              Enable All
            </Button>
          </div>

          <div className="filter-item action-btn-item">
            <label className="field-label">&nbsp;</label>
            <Button danger className="secondary-btn w-full" onClick={handleClearAll}>
              Clear All
            </Button>
          </div>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Module Permissions</h3>
            <p>Configure view, create, update, and delete access for each module.</p>
          </div>
        </div>

        {pageLoading ? (
          <div className="page-empty">
            <Spin size="large" />
          </div>
        ) : !selectedUserId ? (
          <div className="page-empty">
            <Empty description="Please select a user to manage permissions" />
          </div>
        ) : (
          <div className="common-table-wrap">
            <Table
              rowKey="module_id"
              columns={columns}
              dataSource={filteredModules}
              loading={loadingPermissions}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPermission;
